import { useCallback, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { Box } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  Cma_Report_Source_Enum_Enum,
  type Properties_Insert_Input,
} from '@graphql/__generated__/graphql';
import { GET_PROPERTY_TYPES } from '@modules/_shared/common/queries';
import type { RaAddressType } from '@modules/_shared/components/form/RaAddressInput';
import {
  type FormDefinitionType,
  RaForm,
  type RaFormOnChange,
} from '@modules/_shared/components/form/RaForm';
import { LeadsAtAddressSuggestions } from '@modules/_shared/components/lead/LeadsAtAddressSuggestions';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { createPropertyTypeOptions } from '@modules/_shared/components/property-form/forms-definitions/propertyDetailsFormDefinition';
import { useAddressSearchInput } from '@modules/_shared/hooks/useAddressSearchInput';
import { useCreateLead } from '@modules/_shared/hooks/useCreateLead';
import type { LeadAtAddress } from '@modules/_shared/hooks/useLeadsByAddress';
import { useSingleFlight } from '@modules/_shared/hooks/useSingleFlight';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useCreateCMAReport } from '@modules/create-cma-report/utils';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { getBaseCmaEventProperties } from '@shared/analytics/posthog-helpers';
import { useLocale } from '@shared/hooks/locale';

type CMAFromScratchForm = RaAddressType &
  Pick<Properties_Insert_Input, 'property_type_id'> & {
    main_type: any;
  };

type CreateCMAFromScratchProps = {
  onClose: () => void;
  onCreated: (newCMAReportId: string) => void;
  handleCreateCMA: (leadId: string) => Promise<string | undefined>;
};

type LeadSuggestionsSectionProps = {
  onSelect: (lead: LeadAtAddress) => void;
  creatingLeadId: string | null;
};

// Rendered inside RaForm (so it shares the form context) but kept out of the
// form definition: it's a suggestion panel, not a form field. RaAddressInput
// writes the address via setValue with a leading-dot name (e.g. ".route"), so a
// name-scoped useWatch never receives the update — we watch the whole form and
// memoize the address subset so editing the property-type fields doesn't
// re-trigger the debounced lead search.
const LeadSuggestionsSection = ({
  onSelect,
  creatingLeadId,
}: LeadSuggestionsSectionProps) => {
  const { control } = useFormContext<CMAFromScratchForm>();
  const values = useWatch({ control });
  const address = useAddressSearchInput(values);

  return (
    <LeadsAtAddressSuggestions
      address={address}
      onSelect={onSelect}
      creatingLeadId={creatingLeadId}
      sx={{ px: 2, pb: { xs: 10, sm: 2 } }}
    />
  );
};

const CreateCMAFromScratch = ({
  onClose,
  onCreated,
  handleCreateCMA,
}: CreateCMAFromScratchProps) => {
  const { countryCode, t } = useLocale();
  const { me } = useAppData();
  const { track } = useAnalytics();

  const [leadErrorLike, _leadLoading, createNewLead, resetLeadError] =
    useCreateLead();
  const [
    cmaReportErrorLike,
    _cmaReportLoading,
    createCMAReport,
    resetCmaReportError,
  ] = useCreateCMAReport();

  const { data } = useQuery(GET_PROPERTY_TYPES);
  const property_types = data?.property_types ?? [];

  // Selecting an existing lead at the typed address creates the CMA from that
  // lead (the old "From lead" tab behaviour), then closes the drawer. A single
  // in-flight guard is shared with the from-scratch submit so the two paths can
  // never start a second creation while one is already running.
  const [creatingLeadId, setCreatingLeadId] = useState<string | null>(null);
  const { run: runCreation } = useSingleFlight();
  const handleSelectLead = useCallback(
    async (lead: LeadAtAddress) => {
      await runCreation(async () => {
        setCreatingLeadId(lead.id);

        try {
          await handleCreateCMA(lead.id);
        } finally {
          setCreatingLeadId(null);
        }
      });
    },
    [handleCreateCMA, runCreation],
  );

  const fromScratchFormDefinition: FormDefinitionType<CMAFromScratchForm> = ({
    t,
  }) => [
    {
      name: 'category title',
      label: t('Property address'),
      type: 'category-title',
    },
    {
      label: t('Address'),
      type: 'address',
      countryRestriction: countryCode,
      requiredFields: () => ({
        country_code: true,
        country: true,
      }),
    },
    {
      name: 'main_type',
      label: t('Property main type'),
      type: 'select',
      options: () =>
        createPropertyTypeOptions(
          property_types,
          pt => pt.type === 'main',
          'name',
        ),
      required: true,
    },
    {
      name: 'property_type_id',
      label: t('Property type'),
      type: 'select',
      options: ({ main_type }) =>
        createPropertyTypeOptions(
          property_types,
          pt => pt.main_type === main_type && pt.type === 'simple',
          'id',
        ),
      disabled: ({ main_type }) => main_type == null,
      required: true,
    },
  ];

  const onChangeHandler: RaFormOnChange<CMAFromScratchForm> = useCallback(
    (_formData, name, _type, helpers) => {
      if (name === 'main_type') {
        helpers.setValue('property_type_id', null, {
          shouldDirty: true,
        });
      }
    },
    [],
  );

  const handleSubmit = async (values: CMAFromScratchForm) => {
    // Don't start a from-scratch creation while a lead-based one is in flight.
    return runCreation(async () => {
      const startTime = Date.now();

      const createdLeadResult = await createNewLead(
        {
          property: {
            lat: values.lat,
            lng: values.lng,
            route: values.route,
            street_number: values.street_number,
            postcode: values.postcode,
            state: values.state,
            locality: values.locality,
            country_code: values.country_code,
            property_type_id: values.property_type_id,
          },
        },
        false,
      );

      if (!createdLeadResult) {
        return null;
      }

      const newCMAReportId = await createCMAReport({
        leadId: createdLeadResult.leadId,
        // In this case we send the property_id to prevent duplicating the property in the database. It is not necessary because the property cannot be modified by someone else.
        propertyId: createdLeadResult.propertyId,
      });

      if (!newCMAReportId) {
        return null;
      }

      const baseProperties = getBaseCmaEventProperties(me, newCMAReportId);

      if (baseProperties) {
        track(ANALYTICS_EVENTS.CMA_CREATED, {
          ...baseProperties,
          source: Cma_Report_Source_Enum_Enum.CmaFromScratch,
          lead_id: createdLeadResult.leadId,
          property_id: createdLeadResult.propertyId,
          time_to_create_ms: Date.now() - startTime,
        });
      }

      onCreated(newCMAReportId);
    }, null);
  };

  const handleResetError = () => {
    resetLeadError();
    resetCmaReportError();
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <RaForm
          formDefinition={fromScratchFormDefinition}
          onSubmit={handleSubmit}
          onCancel={onClose}
          submitButtonText={t('Create')}
          onChange={onChangeHandler}
          readOnly={creatingLeadId != null}
        >
          <LeadSuggestionsSection
            onSelect={handleSelectLead}
            creatingLeadId={creatingLeadId}
          />
        </RaForm>
      </Box>

      <MutationErrorSnackbar
        error={leadErrorLike || cmaReportErrorLike}
        onClose={handleResetError}
      />
    </>
  );
};

export default CreateCMAFromScratch;
